/* eslint-disable no-console */
import BookingDetailsForBlocker from './BookingDetailsForBlocker';
import BookingDetailsForNonRoomRental from './BookingDetailsForNonRoomRental';
import BookingDetailsForRoomRental from './BookingDetailsForRoomRental';
import BookingDetailsForVenueReservation from './BookingDetailsForVenueReservation';
import { useNavigate, useParams } from 'react-router';
import { useGetBookingDetailsQuery } from '../../../api/booking/bookingApiSlice';
import LoadingPage from '../../common/LoadingPage';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CustomButton from '../../../components/CustomButton';
import { spaceOf } from '../../../utils/useCases';

const BookingDetailsPage = () => {
    const { bookingId } = useParams<{ bookingId: string }>();
    const navigate = useNavigate();
    const { t } = useTranslation();
    // One order, one query. This is the only place that decides how fresh the
    // detail has to be: the use-case children below subscribe to this same
    // query (same args, same cache entry) instead of firing their own, so a
    // detail page opens with exactly one GET /order/:id/room-rental.
    const { data: bookingDetails, isLoading, isError } = useGetBookingDetailsQuery(
        { orderId: bookingId ?? '' },
        { refetchOnMountOrArgChange: true, refetchOnReconnect: true },
    );

    if (isLoading) return <LoadingPage />;

    // `!bookingDetails?.id`, not `!bookingDetails`: the room-rental handler
    // answers 200 with `{}` for an order whose reservation item is empty
    // (`cleanUpRoomRentalResponse`, helper.ts:824), and `{}` is truthy — it
    // would reach the children as a booking with no fields, and the children's
    // "do I have this order?" render gate would then never open.
    if (isError || !bookingDetails?.id) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 'calc(100vh - 200px)',
                    gap: 2,
                    textAlign: 'center',
                    px: 3,
                }}
            >
                <Typography variant={'h5'} color={'text.primary'} fontWeight={600}>
                    {t('booking_details.not_found.title')}
                </Typography>
                <Typography variant={'body1'} color={'text.secondary'}>
                    {t('booking_details.not_found.message')}
                </Typography>
                <CustomButton title={t('booking_details.not_found.back_to_bookings')} onClick={() => navigate('/rental-bookings')} isFilled={true} />
            </Box>
        );
    }

    if (bookingDetails?.isFakeForBlocking) {
        return <BookingDetailsForBlocker />;
    }
    const flow = spaceOf(bookingDetails?.useCase)?.flow;
    if (flow === 'event') {
        return <BookingDetailsForVenueReservation />;
    }
    if (flow === 'stay') {
        return <BookingDetailsForRoomRental />;
    }
    return <BookingDetailsForNonRoomRental />;
};

export default BookingDetailsPage;
